import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import classNames from 'classnames'
import { useRef, type ReactNode } from 'react'
import { getResourceGlyph } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Icon } from '@opallabs/lapidary'
import { useDraggableCalendarItem } from '../../dnd'
import { type PlacedBar } from '../../laneLayout'
import { withCalendarItemMenu, type CalendarMenuProps } from '../../menu'
import { CalendarSizes } from '../../sizes'
import { type CalendarItem } from '../../types'
import { barPositionStyle, itemColorStyle } from '../inlineStyles'
import * as styles from '../styles'
import { weekBreakLabel } from '../weekBreak'

export type MomentBarProps = Readonly<{
  bar: PlacedBar
  color?: string
  /** @default false */
  isDimmed?: boolean
  /**
   * The pointer or the keyboard is on this moment, here or on another week
   * row. A moment drawn in four places is still one thing, so every piece of
   * it answers together.
   *
   * @default false
   */
  isHovered?: boolean
  /**
   * This moment's panel is open. Drawn in the design's focus state on every
   * piece of the moment.
   *
   * @default false
   */
  isOpen?: boolean
  /**
   * Only the piece a moment starts on can be picked up. A moment that crosses a
   * week boundary is drawn once per row, and two handles on one moment would
   * make the same drag mean two different things.
   *
   * @default false
   */
  isDraggable?: boolean
  /** The right-click menu on the bar. Absent means it has none. */
  menu?: CalendarMenuProps
  onClick?: (item: CalendarItem) => void
  /** Says the pointer or the keyboard has arrived on this bar, or left it. */
  onHoverChange?: (isHovered: boolean) => void
  /** The workflow mark the moment has earned, drawn after its name. */
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
}>

/** A moment, drawn across every day it covers inside one week row. */
export const MomentBar: React.FC<MomentBarProps> = ({
  bar,
  color,
  isDimmed = false,
  isDraggable = false,
  isHovered = false,
  isOpen = false,
  menu,
  onClick,
  onHoverChange,
  renderStatusIcon,
}) => {
  const { isEnd, isStart, item } = bar
  const theme = useTheme()
  const ref = useRef<HTMLButtonElement>(null)

  useDraggableCalendarItem({ color, isEnabled: isDraggable, item, ref })

  return withCalendarItemMenu(
    menu,
    item,
    <button
      ref={ref}
      className={classNames(styles.selectors.bar, {
        [styles.stateVariables.clippedEnd]: !isEnd,
        [styles.stateVariables.clippedStart]: !isStart,
        [styles.stateVariables.dimmed]: isDimmed,
        [styles.stateVariables.draggable]: isDraggable,
        [styles.stateVariables.hovered]: isHovered,
        [styles.stateVariables.open]: isOpen,
      })}
      style={{ ...barPositionStyle(bar), ...itemColorStyle(color) }}
      type="button"
      onBlur={() => onHoverChange?.(false)}
      onClick={() => onClick?.(item)}
      onFocus={() => onHoverChange?.(true)}
      onPointerEnter={() => onHoverChange?.(true)}
      onPointerLeave={() => onHoverChange?.(false)}
    >
      <Icon
        ariaHidden={true}
        glyph={getResourceGlyph(theme, item.kind, 'outlined')}
        size={CalendarSizes.barIconSize}
      />
      {item.isPrivate && (
        <Icon
          ariaLabel="Private"
          glyph="gem-locked"
          size={CalendarSizes.barIconSize}
        />
      )}
      <span className={styles.selectors.barTitle}>{item.title}</span>
      {!!item.childCount && (
        <span className={styles.selectors.barCount}>{item.childCount}</span>
      )}
      {renderStatusIcon?.(item, CalendarSizes.barIconSize)}
      {/* Nothing on a squared edge says which way the rest of the moment
          went, so the bar's own name says it. */}
      {(!isStart || !isEnd) && (
        <VisuallyHidden>{weekBreakLabel({ isEnd, isStart })}</VisuallyHidden>
      )}
    </button>
  )
}
